import React, { useCallback, useEffect, useState } from 'react';
import { FileText, ExternalLink, Plus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { formatAED, formatDate } from '@/lib/format';
import { CONTRACT_STATUS_COLOR } from '@/lib/constants';
import ContractSlideOver from '@/components/contracts/ContractSlideOver';

export default function LinkedContracts({ contact }) {
    const { profile } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState(false);
    const [editing, setEditing] = useState(null);
    const [opts, setOpts] = useState({ contacts: [], developers: [], pms: [], projects: [] });

    const load = useCallback(async () => {
        if (!contact?.id) return;
        setLoading(true);
        const { data } = await supabase.from('contracts').select('*').eq('related_contact_id', contact.id).is('deleted_at', null).order('created_at', { ascending: false });
        setItems(data || []);
        setLoading(false);
    }, [contact?.id]);
    useEffect(() => { load(); }, [load]);

    const openAdd = async () => {
        const [ct, dv, pm, pr] = await Promise.all([
            supabase.from('pipeline_contacts').select('id,full_name').is('deleted_at', null).order('full_name'),
            supabase.from('developers').select('id,company_name').is('deleted_at', null).order('company_name'),
            supabase.from('property_managers').select('id,company_name').is('deleted_at', null).order('company_name'),
            supabase.from('developer_projects').select('id,project_name').is('deleted_at', null).order('project_name'),
        ]);
        setOpts({ contacts: ct.data || [], developers: dv.data || [], pms: pm.data || [], projects: pr.data || [] });
        setEditing({ related_contact_id: contact.id, title: '', contract_type: 'MOU', status: 'Active', contract_date: new Date().toISOString().slice(0, 10) });
        setShow(true);
    };

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="linked-contracts">
            <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">Linked contracts</div>
                <button onClick={openAdd} className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-gray-300 hover:border-aks-gold text-[11px] font-semibold text-aks-navy" data-testid="link-contract-btn"><Plus className="h-3.5 w-3.5" /> Add</button>
            </div>
            {loading ? (
                <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <div key={i} className="aks-skeleton h-12 rounded-md" />)}</div>
            ) : items.length === 0 ? (
                <div className="py-4 text-center text-[12px] text-gray-400">No contracts linked</div>
            ) : (
                <div className="space-y-2">
                    {items.map((c) => {
                        const sc = CONTRACT_STATUS_COLOR[c.status] || '#9CA3AF';
                        return (
                            <div key={c.id} className="flex items-center gap-3 p-2 rounded-md border border-gray-100 hover:border-aks-gold/40 hover:bg-aks-gold-soft/20" data-testid={`linked-contract-${c.id}`}>
                                <div className="h-8 w-8 rounded-md bg-aks-navy/10 text-aks-navy flex items-center justify-center"><FileText className="h-4 w-4" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[13px] text-aks-text truncate">{c.title}</div>
                                    <div className="text-[11px] text-gray-500">{c.contract_type} · {c.expiry_date ? `Exp: ${formatDate(c.expiry_date)}` : 'No expiry'} · {c.value_aed ? formatAED(c.value_aed) : '—'}</div>
                                </div>
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: `${sc}1A`, color: sc }}>{c.status}</span>
                                {c.file_url && <a href={c.file_url} target="_blank" rel="noreferrer" className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-gray-300"><ExternalLink className="h-3 w-3" /></a>}
                            </div>
                        );
                    })}
                </div>
            )}
            <ContractSlideOver open={show} onClose={() => setShow(false)} contract={editing} onSaved={load} contacts={opts.contacts} developers={opts.developers} pms={opts.pms} projects={opts.projects} />
        </div>
    );
}
